import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/images";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Trophy, Users, Clock, Sprout, Ruler, LifeBuoy, ArrowRight, CheckCircle2 } from "lucide-react";
import pitchAerial from "@/assets/pitch-aerial.jpg";
import comboInAction from "@/assets/combo-in-action.jpeg";
import miniLineMarking from "@/assets/mini-line-marking.jpg";
import frxGolf from "@/assets/frx-golf.webp";
import frxHero from "@/assets/frx-hero.webp";

const SPORT_IMAGES = {
  gaa: "https://images.pexels.com/photos/36709125/pexels-photo-36709125.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  soccer: "https://images.pexels.com/photos/33210166/pexels-photo-33210166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  rugby: "https://images.pexels.com/photos/5585965/pexels-photo-5585965.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const SPORTS = [
  { key: "gaa", title: "GAA Clubs", img: SPORT_IMAGES.gaa, copy: "Crisp, accurate markings and healthy playing surfaces for Gaelic football and hurling pitches." },
  { key: "soccer", title: "Soccer & Football", img: SPORT_IMAGES.soccer, copy: "Full 11-a-side and training pitch marking with consistent, tidy lines every week." },
  { key: "rugby", title: "Rugby Clubs", img: SPORT_IMAGES.rugby, copy: "Reliable pitch marking and mowing for match and training grounds through the season." },
  { key: "golf", title: "Golf Facilities", img: frxGolf, copy: "Precise, low-noise robotic mowing for fairways, roughs and amenity areas." },
];

const BENEFITS = [
  { icon: Sprout, title: "Consistent quality", copy: "Regular, precise cutting helps maintain healthy, well-presented pitches." },
  { icon: Clock, title: "Time for volunteers", copy: "Free up staff and volunteer time for other club activities." },
  { icon: Ruler, title: "Accurate line marking", copy: "Repeatable, tidy markings for match and training days." },
  { icon: LifeBuoy, title: "Ongoing support", copy: "Servicing and priority support to keep equipment running." },
];

const CLUB_TYPES = ["GAA Clubs", "Football Clubs", "Rugby Clubs", "Golf Facilities", "Schools & Colleges", "Running Tracks", "Community Facilities", "Local Authorities"];

export default function Clubs() {
  const [enq, setEnq] = useState(false);
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand-ink text-white">
        <div className="absolute inset-0 opacity-40"><img src={pitchAerial} alt="Aerial view of a freshly marked GAA pitch" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-brand-ink to-brand-ink/50" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Clubs &amp; Sports Facilities</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Robotic mowing &amp; line marking for sports clubs</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">Autonomous pitch marking and mowing for GAA, soccer, rugby, golf and school grounds across Ireland — with expert advice, professional installation and ongoing support.</p>
          <Button onClick={() => setEnq(true)} className="mt-8 bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="clubs-hero-enquire">Arrange a consultation</Button>
        </div>
      </section>

      {/* SEO intro */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Automated pitch care for Irish sports grounds</h2>
          <div className="mt-5 space-y-4 text-muted-foreground leading-relaxed">
            <p>Robotic Lawnmower Experts helps sports clubs, schools and facilities across Ireland keep their grounds in top condition with robotic mowing and autonomous line marking. From a single GAA pitch to multi-field complexes, our solutions deliver consistent, professional results while freeing up valuable staff and volunteer time.</p>
            <p>Our autonomous line markers create accurate, repeatable pitch markings for Gaelic football, hurling, soccer, rugby and other sports — mapping your fields in minutes and marking crisp lines with less paint and less labour. Paired with robotic mowing, your grounds stay match-ready throughout the season.</p>
            <p>Whether you manage a local club pitch, a school playing field or a golf facility, we'll advise on the right equipment, handle professional installation and provide ongoing servicing and priority support.</p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/line-marking" data-testid="clubs-link-linemarking">Explore line marking <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/commercial" data-testid="clubs-link-commercial">Commercial mowing <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
          <img src={comboInAction} alt="FJD robotic line marker marking a sports pitch" className="w-full h-72 lg:h-96 object-cover" data-testid="clubs-intro-image" />
        </div>
      </section>

      {/* Sports we serve — image cards */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Sports we mark &amp; maintain</h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Purpose-built robotic mowing and line marking for the codes played across Irish clubs and schools.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SPORTS.map((s) => (
              <div key={s.key} className="group rounded-2xl overflow-hidden border border-brand-navy/10 bg-white" data-testid={`clubs-sport-${s.key}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={s.img} alt={`${s.title} pitch`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-lg text-brand-navy">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature: line marking */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
          <img src={miniLineMarking} alt="Robotic line marker painting a white line on a pitch" className="w-full h-72 lg:h-96 object-cover" data-testid="clubs-feature-marking-image" />
        </div>
        <div>
          <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Autonomous line marking</span>
          <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Perfect pitch lines, week after week</h2>
          <p className="mt-4 text-muted-foreground">GPS-guided robotic line markers map your pitches and paint accurate, repeatable lines — for soccer, rugby, GAA, American football and more. Switch between field layouts, mark multiple pitches from one saved map, and reduce paint usage compared to manual marking.</p>
          <ul className="mt-6 space-y-2">
            {["Handheld mapping in minutes — no driving the perimeter", "Consistent, tidy lines for match and training", "Multiple sports and field sizes from saved maps", "Less paint and less labour than manual marking"].map((f) => (
              <li key={f} className="flex items-start gap-2 text-brand-ink"><CheckCircle2 className="h-5 w-5 text-brand-navy mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Feature: mowing */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-last lg:order-first">
            <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Robotic mowing</span>
            <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Healthy, match-ready surfaces</h2>
            <p className="mt-4 text-muted-foreground">Autonomous mowers keep club and school grounds neatly cut with quiet, low-emission operation. Regular mulching promotes a healthy sward, while robotic scheduling means your pitches stay presentable without tying up staff and volunteers.</p>
            <ul className="mt-6 space-y-2">
              {["Quiet operation suited to clubs and schools", "Consistent cutting for healthier grass", "Frees up staff and volunteer time", "Priority servicing and support plans"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-brand-ink"><CheckCircle2 className="h-5 w-5 text-brand-navy mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
            <img src={frxHero} alt="Robotic mower maintaining a sports facility" className="w-full h-72 lg:h-96 object-cover" data-testid="clubs-feature-mowing-image" />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Benefits for your club</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><b.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.copy}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <h3 className="font-display font-semibold text-lg text-brand-ink">Who we work with</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {CLUB_TYPES.map((c) => <span key={c} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-navy/10 text-brand-ink"><Trophy className="h-4 w-4 text-brand-navy" /> {c}</span>)}
          </div>
          <p className="mt-6 text-sm text-muted-foreground max-w-3xl">We're happy to provide equipment specifications and technical documentation to support your planning and any grant or funding applications. Contact us to discuss what would suit your facility.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="rounded-3xl bg-brand-navy text-white p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-2xl">Ready to upgrade your pitch?</h3>
            <p className="mt-2 text-white/80">Arrange a site visit and we'll advise on the right mowing and marking setup for your club.</p>
          </div>
          <Button onClick={() => setEnq(true)} size="lg" className="bg-white hover:bg-white/90 text-brand-navy font-semibold shrink-0" data-testid="clubs-cta-enquire">Arrange a consultation</Button>
        </div>
      </section>

      <EnquiryDialog open={enq} onOpenChange={setEnq} type="commercial" sourcePage="clubs" />
    </div>
  );
}
